import React, { useState } from 'react'

import { Box, Tabs } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'
import { RateConfigurationsPageDocument } from 'generated/resources/graphql'

import NotFound from 'modules/core/components/NotFound'
import { useUnsafeCallback } from 'modules/core/hooks/useCallback'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'
import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import RateConfigurationsDataTable from 'modules/expenses/pages/settings/RateConfigurationsPage/DataTable'
import RateFormModal from 'modules/expenses/pages/settings/RateConfigurationsPage/RateFormModal'
import {
  ExpensableRateConfigurationResourceType,
  ExpensableRateConfigurationResourceTypeEnum,
} from 'modules/expenses/types/Expensable'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

interface RateConfigurationsPageProps {
  resourceType: ExpensableRateConfigurationResourceType
}

const TABS = {
  ACTIVE: 'active',
  ARCHIVED: 'archived',
}

const RateConfigurationsListPage = ({
  resourceType,
  children,
}: React.PropsWithChildren<RateConfigurationsPageProps>) => {
  const [showModal, toggleModal] = useState(false)
  const { company } = useCurrentSession()

  const [tab, setTab] = useState(TABS.ACTIVE)

  const { data, loading } = useQuery(RateConfigurationsPageDocument, {
    variables: {
      featureId: Features.expenses_per_diems,
      companyId: company.id,
    },
  })

  const trackEvent = () => {
    track(trackingEvents.expenses.clickAddNewPerDiemRate)
  }

  const trackMileageEvent = () => {
    track(trackingEvents.expenses.clickAddNewMileageRate)
  }
  const havePerDiemFeature =
    data?.companies.featureAvailabilitiesConnection.nodes[0]?.available ?? false

  const onClickAddRate = useUnsafeCallback(() => {
    resourceType === ExpensableRateConfigurationResourceTypeEnum.PerDiem
      ? trackEvent()
      : trackMileageEvent()
    toggleModal(true)
  }, [toggleModal])

  if (
    !loading &&
    resourceType === ExpensableRateConfigurationResourceTypeEnum.PerDiem &&
    !havePerDiemFeature
  )
    return <NotFound />

  return (
    <ResponsivePage.Body loading={loading} error={null} variant="full">
      {children}
      <Box gap="s32" paddingTop="s32">
        <Tabs currentId={tab} onTabClicked={(tabId) => setTab(tabId)}>
          <Tabs.Tab
            id={TABS.ACTIVE}
            label={i18n.t('expenses_v2.settings.rate_configurations.tabs.active')}
          >
            <RateConfigurationsDataTable
              resourceType={resourceType}
              onClickAddRate={onClickAddRate}
            />
          </Tabs.Tab>
          <Tabs.Tab
            id={TABS.ARCHIVED}
            label={i18n.t('expenses_v2.settings.rate_configurations.tabs.archived')}
          >
            <RateConfigurationsDataTable
              resourceType={resourceType}
              archivedOnly
              onClickAddRate={onClickAddRate}
            />
          </Tabs.Tab>
        </Tabs>
        {showModal && (
          <RateFormModal
            title={
              resourceType === ExpensableRateConfigurationResourceTypeEnum.PerDiem
                ? i18n.t('expenses_v2.settings.rate_configurations.create_modal.title')
                : i18n.t('expenses_v2.settings.rate_configurations.create_modal.mileage_title')
            }
            description={
              resourceType === ExpensableRateConfigurationResourceTypeEnum.PerDiem
                ? i18n.t('expenses_v2.settings.rate_configurations.create_modal.description')
                : i18n.t(
                    'expenses_v2.settings.rate_configurations.create_modal.mileage_description'
                  )
            }
            onClose={() => toggleModal(false)}
            resourceType={resourceType}
          />
        )}
      </Box>
    </ResponsivePage.Body>
  )
}

export default RateConfigurationsListPage
